import * as React from "react";
import { BaseTypeLiterals } from "../../../../../application/domain/base/base-type";
import { ApplicationStatusTag } from "../../../../molecules/status-tag";
import {
  ApplicationEventType,
  ContentModified,
  InternalCommentAdded,
  StatusUpdated,
  MessageAdded,
  IssueFilesAdded,
  IssueFilesCancelled,
  IssueFilesFirstDownloaded,
  PaymentAdded,
  PaymentPaid,
  PaymentPayeasyRequested,
  PaymentExpired,
  PaymentRefunded,
  PaymentDeficiencyAdjusted,
  PaymentExceedingAdjusted,
  PaymentWithdrawn,
  TenantProcessResultUpdated,
  RemovalTaskCanceled,
  RemovalTaskScheduled,
} from "@graffer-inc/cloud-gov-core";
import { ApplicationMessageJSON } from "../../../../../application/domain/application/application-message";
import { ApplicationEntity } from "../../../../../application/domain/application/application-entity";
import { Link } from "react-router-dom";
import { SmartApplyR } from "../../route";
import { ProcedureEntity } from "../../../../../application/domain/procedures/procedure-entity";
import { ApplicationHistory } from "../../../../../application/domain/application/application-history";
import { MessageAndContent } from "../../../../organisms/smart-apply/application-detail/message-and-content";
import { GrafferGqlResult } from "@graffer-inc/smart-apply";
import { ResourceFetchError } from "../../../../organisms/common/resource-fetch-error";
import { ContentModifiedView } from "./content-modified";
import dayjs from "../../../../../utils/date-utils";
import { ApplicationInternalCommentJSON } from "../../../../../application/domain/application/application-internal-comment";
import { SmartApplyErrorCodeLiterals } from "@graffer-inc/smart-apply";
import { RemovalTaskScheduledView } from "./removal-scheduled";

export function matchEvent<V extends string>(
  value: V,
  matcher: BaseTypeLiterals<V, (v: any) => React.ReactNode>,
): (v: any) => React.ReactNode {
  return matcher[value];
}

const TimelineItem: React.FC<{
  app: ApplicationEntity;
  event: ApplicationEventType;
  message?: string;
  operatorName?: string | null;
  procedure: ProcedureEntity;
  histories: GrafferGqlResult<ApplicationHistory[]>;
}> = ({ app, event, message, operatorName, procedure, histories }) => {
  return (
    <div className="timeline-item">
      <div className="timeline-marker" />
      <div className="timeline-content">
        <p className="heading">
          {dayjs(event.createdAt).format("YYYY年MM月DD日(ddd) HH:mm")}
          {operatorName && " / "}
          {operatorName}
        </p>
        <div>
          {matchEvent(event.eventType, {
            MessageAdded: (e: MessageAdded) => {
              return (
                <>
                  <p className="has-text-weight-bold">
                    申請者にメッセージを送りました。
                  </p>
                  <article
                    className="media notification has-text-weight-normal mt-3 is-size-7 pl-3 pr-3 pt-4 pb-4"
                    style={{
                      maxWidth: "600px",
                    }}
                  >
                    <div className="media-content">
                      <div className="content">
                        <p>
                          {e.data.content &&
                            e.data.content.split(`\n`).map((s) => (
                              <>
                                {s}
                                <br />
                              </>
                            ))}
                        </p>
                      </div>
                    </div>
                  </article>
                </>
              );
            },
            Created: () => (
              <p className="has-text-weight-bold">申請が到達しました。</p>
            ),
            StatusUpdated: (ev: StatusUpdated) => (
              <>
                <p className="has-text-weight-bold">
                  ステータスを
                  <ApplicationStatusTag status={ev.data.status} />
                  に変更しました。
                  {!!ev.data.skipNotification && (
                    <span className="heading">
                      メール通知をスキップしました。
                    </span>
                  )}
                </p>

                {message && <MessageAndContent data={{ message }} />}
              </>
            ),
            InternalCommentAdded: (event: InternalCommentAdded) => (
              <>
                <p className="has-text-weight-bold">
                  内部コメントを登録しました。
                </p>
                <article
                  className="media notification has-text-weight-normal mt-3 is-size-7 pl-3 pr-3 pt-4 pb-4"
                  style={{
                    maxWidth: "600px",
                  }}
                >
                  <div className="media-content">
                    <div className="content">
                      <p>
                        {event.data.content &&
                          event.data.content.split(`\n`).map((s) => (
                            <>
                              {s}
                              <br />
                            </>
                          ))}
                      </p>
                    </div>
                  </div>
                </article>
              </>
            ),
            ContentModified: (event: ContentModified) => {
              return histories.matchResult<
                SmartApplyErrorCodeLiterals | "",
                React.ReactNode
              >(
                {
                  rootPath: "applicationHistories",
                  errorTypes: "",
                },
                {
                  loading: undefined,
                  success: (
                    actualHistories: ApplicationHistory[],
                  ): JSX.Element => {
                    const histIdx = actualHistories.findIndex(
                      (h) => h.id === event.data.historyId,
                    );
                    return (
                      <ContentModifiedView
                        event={event}
                        procedure={procedure}
                        previous={actualHistories[histIdx - 1]}
                        history={actualHistories[histIdx]}
                        application={app}
                      />
                    );
                  },
                  unexpected: (
                    <ResourceFetchError
                      title={"予期しないエラーが発生して表示できませんでした。"}
                      subtitle={
                        "しばらく時間をおいて再度実行するか、管理者にお問い合わせください。"
                      }
                    />
                  ),
                },
              );
            },
            PaymentAdded: (e: PaymentAdded) => (
              <>
                <p className="has-text-weight-bold">
                  追加の
                  <Link
                    to={SmartApplyR.ApplicationPaymentDetail.link({
                      id: e.applicationId,
                      index: e.data.paymentIndex,
                    })}
                  >
                    支払い
                  </Link>
                  を依頼しました。
                </p>
                <MessageAndContent data={e.data} />
              </>
            ),
            PaymentPaid: (e: PaymentPaid) => (
              <p className="has-text-weight-bold">
                申請者が
                <Link
                  to={SmartApplyR.ApplicationPaymentDetail.link({
                    id: e.applicationId,
                    index: e.data.paymentIndex,
                  })}
                >
                  支払い
                </Link>
                を行いました。
              </p>
            ),
            PaymentExpired: (e: PaymentExpired) => (
              <p className="has-text-weight-bold">
                発行された
                <Link
                  to={SmartApplyR.ApplicationPaymentDetail.link({
                    id: e.applicationId,
                    index: e.data.paymentIndex,
                  })}
                >
                  支払い情報
                </Link>
                の期限が切れました。
              </p>
            ),
            PaymentPayeasyRequested: (e: PaymentPayeasyRequested) => (
              <p className="has-text-weight-bold">
                申請者が
                <Link
                  to={SmartApplyR.ApplicationPaymentDetail.link({
                    id: e.applicationId,
                    index: e.data.paymentIndex,
                  })}
                >
                  支払い情報
                </Link>
                を発行しました。
              </p>
            ),
            PaymentRefunded: (e: PaymentRefunded) => (
              <>
                <p className="has-text-weight-bold">
                  <Link
                    to={SmartApplyR.ApplicationPaymentDetail.link({
                      id: e.applicationId,
                      index: e.data.paymentIndex,
                    })}
                  >
                    支払い
                  </Link>
                  を返金しました。
                </p>
                <MessageAndContent data={e.data} />
              </>
            ),
            PaymentDeficiencyAdjusted: (e: PaymentDeficiencyAdjusted) => (
              <>
                <p className="has-text-weight-bold">
                  <Link
                    to={SmartApplyR.ApplicationPaymentDetail.link({
                      id: e.applicationId,
                      index: e.data.paymentIndex,
                    })}
                  >
                    入金不足を解消
                  </Link>
                  しました。
                </p>
                {message && <MessageAndContent data={{ message: message }} />}
              </>
            ),
            PaymentExceedingAdjusted: (e: PaymentExceedingAdjusted) => (
              <>
                <p className="has-text-weight-bold">
                  <Link
                    to={SmartApplyR.ApplicationPaymentDetail.link({
                      id: e.applicationId,
                      index: e.data.paymentIndex,
                    })}
                  >
                    過入金を解消
                  </Link>
                  しました。
                </p>
                {message && <MessageAndContent data={{ message: message }} />}
              </>
            ),
            PaymentWithdrawn: (e: PaymentWithdrawn) => (
              <>
                <p className="has-text-weight-bold">
                  <Link
                    to={SmartApplyR.ApplicationPaymentDetail.link({
                      id: e.applicationId,
                      index: e.data.paymentIndex,
                    })}
                  >
                    支払い
                  </Link>
                  の依頼を取下げました。
                </p>
                <MessageAndContent data={e.data} />
              </>
            ),
            IssueFilesAdded: (e: IssueFilesAdded) => (
              <p className="has-text-weight-bold">
                交付物「{e.data.name}」を追加しました。
              </p>
            ),
            IssueFilesCancelled: (e: IssueFilesCancelled) => (
              <p className="has-text-weight-bold">
                交付物「{app.issueFiles[e.data.fileIndex].name}
                」をキャンセルしました。
              </p>
            ),
            IssueFilesFirstDownloaded: (e: IssueFilesFirstDownloaded) => (
              <p className="has-text-weight-bold">
                交付物「{app.issueFiles[e.data.fileIndex].name}
                」が申請者によりダウンロードされました。
              </p>
            ),
            TenantProcessResultUpdated: (_: TenantProcessResultUpdated) => (
              <p className="has-text-weight-bold">管理情報が更新されました。</p>
            ),
            StatusReverted: (ev: StatusUpdated) => (
              <>
                <p className="has-text-weight-bold">
                  ステータスを
                  <ApplicationStatusTag status={ev.data.status} />
                  に変更しました。
                </p>
                {message && (
                  <MessageAndContent data={{ internalComment: message }} />
                )}
              </>
            ),
            RemovalTaskScheduled: (e: RemovalTaskScheduled) => (
              <RemovalTaskScheduledView event={e} />
            ),
            RemovalTaskCanceled: (_: RemovalTaskCanceled) => (
              <p className="has-text-weight-bold">
                申請削除タスクがキャンセルされました。
              </p>
            ),
          })(event)}
        </div>
      </div>
    </div>
  );
};

export const EventTimeline: React.FC<{
  app: ApplicationEntity;
  procedure: ProcedureEntity;
  histories: GrafferGqlResult<ApplicationHistory[]>;
  events: GrafferGqlResult<ApplicationEventType[]>;
  internalComments: GrafferGqlResult<ApplicationInternalCommentJSON[]>;
  messages: GrafferGqlResult<ApplicationMessageJSON[]>;
}> = ({ app, procedure, histories, events, internalComments, messages }) => {
  return (
    <>
      {events.matchResult(
        {
          rootPath: "applicationEventsAndMessages",
          errorTypes: "forbidden",
        },
        {
          // TODO: Fixme
          loading: undefined,
          success: (eventTypes: ApplicationEventType[]): React.ReactNode => {
            return (
              <>
                <div className="timeline ml-4">
                  <header className="timeline-header">
                    <span className="tag is-medium">申請到達</span>
                  </header>
                  {eventTypes.map((e) => {
                    let operatorName = "";
                    switch (e.createdBy.userType) {
                      case "CitizenUser":
                        operatorName = "申請者";
                        break;
                      case "System":
                        operatorName = "システム";
                        break;
                      case "OrgUser":
                        operatorName = e.createdBy.name || "職員";
                        break;
                    }
                    let message: string | undefined;
                    if (e.eventType === "StatusUpdated") {
                      message = messages.data!.find(
                        (m) => m.id === e.data.messageId,
                      )?.content;
                    } else if (e.eventType === "StatusReverted") {
                      message = internalComments.data!.find(
                        (ic) => ic.id === e.data.internalCommentId,
                      )?.content;
                    }
                    return (
                      <div key={e.id}>
                        <TimelineItem
                          app={app}
                          procedure={procedure}
                          histories={histories}
                          event={e}
                          message={message}
                          operatorName={operatorName}
                        />
                        {(e.eventType === "StatusUpdated" ||
                          e.eventType === "StatusReverted") && (
                          <div className="timeline-header">
                            <ApplicationStatusTag status={e.data.status} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          },
          forbidden: (
            <div className="columns">
              <div className="column is-8 has-text-left">
                <ResourceFetchError
                  title={"権限がありません。"}
                  subtitle={
                    "タイムラインを表示する権限がありません。管理者にお問い合わせください。"
                  }
                />
              </div>
            </div>
          ),
          unexpected: (
            <div className="columns">
              <div className="column is-8 has-text-left">
                <ResourceFetchError
                  title={"予期しないエラーが発生して表示できませんでした。"}
                  subtitle={
                    "しばらく時間をおいて再度実行するか、管理者にお問い合わせください。"
                  }
                />
              </div>
            </div>
          ),
        },
      )}
    </>
  );
};
